import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Combobox, ETypeOfNotification, InputDate, Modal, setNotification } from 'medere-ui-framework';
import { CashRegisterDTO, CashRegisterFinancialAccountDTO, CashSummaryDTO } from '../../dto/locationcash/LocationCashDTO';
import { MovementTypeOption, MovementResponseDTO, CreateMovementRequestDTO } from '../../dto/FinancialAccountDTO';
import { UsersListItemDTO } from '../../dto/users/UsersDTO';
import { getCashRegistersForAdministration, getCashRegisterFinancialAccounts, getCashSummary } from '../../services/LocationCashController';
import { createMovement, getMovementTypes, listMovements } from '../../services/FinancialAccountController';
import CashSummaryCards from '../LocationDesk/components/CashSummaryCards';
import { getUsersList } from '../../servicesHooks/Users/useUsersService';
import { getFieldErrorMessage, INSUFFICIENT_BALANCE, parseApiError } from '../../services/apiError';
import FieldError from '../../components/FieldError/FieldError';


const PAGE_SIZE = 20;

interface CashRegisterOption {
    locationCashRegisterId: number;
    hospitalLocationId: number | null;
    description: string;
    displayDescription: string;
}

function formatDateTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleString('es-AR');
}

function formatMoney(amount: number | null | undefined): string {
    if (amount == null) return '—';
    return `$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function dateParam(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
}

function startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

export default function CashRenditionView() {
    const dispatch = useDispatch();

    // Selector de caja
    const [registers, setRegisters] = useState<CashRegisterOption[]>([]);
    const [selectedRegister, setSelectedRegister] = useState<CashRegisterOption | null>(null);
    const [loadingRegisters, setLoadingRegisters] = useState(true);

    // Summary
    const [cashSummary, setCashSummary] = useState<CashSummaryDTO | null>(null);

    // Movimientos
    const [movements, setMovements] = useState<MovementResponseDTO[]>([]);
    const [totalMovements, setTotalMovements] = useState(0);
    const [firstMovement, setFirstMovement] = useState(0);
    const [loadingMovements, setLoadingMovements] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [fromDate, setFromDate] = useState<Date>(() => startOfDay(new Date()));
    const [toDate, setToDate] = useState<Date>(() => new Date());
    const [filterAccounts, setFilterAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [selectedFilterAccount, setSelectedFilterAccount] = useState<CashRegisterFinancialAccountDTO | null>(null);
    const [users, setUsers] = useState<UsersListItemDTO[]>([]);
    const [selectedUser, setSelectedUser] = useState<UsersListItemDTO | null>(null);

    // Modal nuevo movimiento
    const [movementTypes, setMovementTypes] = useState<MovementTypeOption[]>([]);
    const [movModalVisible, setMovModalVisible] = useState(false);
    const [movAccounts, setMovAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [movSelectedAccount, setMovSelectedAccount] = useState<CashRegisterFinancialAccountDTO | null>(null);
    const [movType, setMovType] = useState<MovementTypeOption | null>(null);
    const [movAmount, setMovAmount] = useState<number | null>(null);
    const [movDate, setMovDate] = useState<Date>(new Date());
    const [movNotes, setMovNotes] = useState('');
    const [creatingMovement, setCreatingMovement] = useState(false);
    const [movAmountError, setMovAmountError] = useState<string | null>(null);
    const movAmountRef = useRef<HTMLInputElement>(null);

    const registerId = selectedRegister?.locationCashRegisterId ?? null;

    // Cargar cajas, usuarios y tipos de movimiento
    useEffect(() => {
        getCashRegistersForAdministration()
            .then((data: CashRegisterDTO[]) => {
                setRegisters(data.filter(r => r.active).map(r => ({
                    locationCashRegisterId: r.locationCashRegisterId,
                    hospitalLocationId: r.hospitalLocationId ?? null,
                    description: r.description,
                    displayDescription: r.locationDescription
                        ? `${r.description} — ${r.locationDescription}`
                        : r.description,
                })));
            })
            .catch(console.error)
            .finally(() => setLoadingRegisters(false));

        getUsersList(0, 200).then(setUsers).catch(console.error);
        getMovementTypes()
            .then(types => setMovementTypes(types))
            .catch(console.error);
    }, []);

    // Cargar summary y cuentas cuando cambia la caja
    useEffect(() => {
        setCashSummary(null);
        setFilterAccounts([]);
        setSelectedFilterAccount(null);
        if (!selectedRegister) return;

        if (selectedRegister.hospitalLocationId) {
            getCashSummary(selectedRegister.hospitalLocationId)
                .then(setCashSummary)
                .catch(() => setCashSummary(null));
        }

        getCashRegisterFinancialAccounts(selectedRegister.locationCashRegisterId)
            .then(accs => setFilterAccounts(accs.filter(a => a.active)))
            .catch(() => setFilterAccounts([]));
    }, [selectedRegister]);

    // Cargar movimientos
    const loadPage = useCallback((offset: number) => {
        if (!registerId) return;
        setLoadingMovements(true);
        listMovements({
            cashRegisterId: registerId,
            from: dateParam(startOfDay(fromDate)),
            to: dateParam(endOfDay(toDate)),
            financialAccountId: selectedFilterAccount?.financialAccountId,
            userId: selectedUser?.userId,
            offset,
            limit: PAGE_SIZE,
        })
            .then(res => {
                setMovements(res.items);
                setTotalMovements(res.totalCount);
            })
            .catch(console.error)
            .finally(() => setLoadingMovements(false));
    }, [registerId, fromDate, toDate, selectedFilterAccount, selectedUser]);

    useEffect(() => {
        if (!registerId) {
            setMovements([]);
            setTotalMovements(0);
            return;
        }
        setFirstMovement(0);
        loadPage(0);
    }, [loadPage, refreshKey]);

    const onPage = (e: DataTablePageEvent) => {
        setFirstMovement(e.first);
        loadPage(e.first);
    };

    // Nuevo movimiento
    const handleOpenMovModal = async () => {
        if (!registerId) return;
        setMovType(null);
        setMovAmount(null);
        setMovAmountError(null);
        setMovDate(new Date());
        setMovNotes('');
        setMovModalVisible(true);
        try {
            const accounts = await getCashRegisterFinancialAccounts(registerId);
            const active = accounts.filter(a => a.active);
            setMovAccounts(active);
            setMovSelectedAccount(active.find(a => a.isDefault) ?? active[0] ?? null);
        } catch {
            setMovAccounts([]);
            setMovSelectedAccount(null);
        }
    };

    const handleCreateMovement = async () => {
        if (!movSelectedAccount || !movType) return;
        if (movAmount == null || (movType.id !== 4 && movAmount <= 0)) {
            dispatch(setNotification({
                title: 'Monto inválido',
                desc: movType.id === 4 ? 'Ingrese un monto (puede ser negativo para ajuste)' : 'El monto debe ser mayor a 0',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        setCreatingMovement(true);
        try {
            const body: CreateMovementRequestDTO = {
                financialAccountId: movSelectedAccount.financialAccountId,
                movementTypeId: movType.id,
                amount: movAmount,
                dateTime: dateParam(movDate),
                notes: movNotes.trim() || undefined,
                sourceEntity: 'MANUAL',
            };
            await createMovement(body);
            dispatch(setNotification({
                title: 'Movimiento registrado',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            setMovModalVisible(false);
            setRefreshKey(v => v + 1);
        } catch (error) {
            const apiError = parseApiError(error);
            const amountError = apiError.errorCode === INSUFFICIENT_BALANCE
                ? getFieldErrorMessage(apiError, 'amount')
                : undefined;
            if (amountError) {
                setMovAmountError(amountError);
                movAmountRef.current?.focus();
                return;
            }
            dispatch(setNotification({
                title: 'No se pudo registrar el movimiento',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setCreatingMovement(false);
        }
    };

    if (loadingRegisters) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="viewContainer">
            <h1 style={{ margin: '0 0 4px' }}>Rendición de caja</h1>
            <p style={{ color: '#5A6676', margin: '0 0 24px' }}>Seleccione una caja para ver y registrar movimientos.</p>

            <div className="flex align-items-end gap-4 mb-4 flex-wrap">
                <div style={{ width: '180px' }}>
                    <InputDate
                        label="Desde"
                        value={fromDate}
                        onChange={(e: any) => setFromDate(e.value ?? startOfDay(new Date()))}
                    />
                </div>
                <div style={{ width: '180px' }}>
                    <InputDate
                        label="Hasta"
                        value={toDate}
                        onChange={(e: any) => setToDate(e.value ?? new Date())}
                    />
                </div>
                <div style={{ minWidth: '280px' }}>
                    <Combobox
                        label="Caja"
                        list={registers}
                        value={selectedRegister}
                        setValue={(v: CashRegisterOption | null) => setSelectedRegister(v)}
                        optionLabel="displayDescription"
                        placeholder="Seleccionar caja..."
                    />
                </div>
                {registerId && (
                    <>
                        <div style={{ minWidth: '180px' }}>
                            <Combobox
                                label="Cuenta"
                                list={filterAccounts}
                                value={selectedFilterAccount}
                                setValue={(v: CashRegisterFinancialAccountDTO | null) => setSelectedFilterAccount(v)}
                                optionLabel="financialAccountName"
                                placeholder="Todas"
                            />
                        </div>
                        <div style={{ minWidth: '180px' }}>
                            <Combobox
                                label="Usuario"
                                list={users}
                                value={selectedUser}
                                setValue={(v: UsersListItemDTO | null) => setSelectedUser(v)}
                                optionLabel="username"
                                placeholder="Todos"
                            />
                        </div>
                    </>
                )}
            </div>

            {registerId ? (
                <div>
                    {cashSummary?.allowsSummary && (
                        <div className="mb-4">
                            <CashSummaryCards summary={cashSummary} />
                        </div>
                    )}

                    <div className="flex align-items-center justify-content-between mb-3">
                        <h3 style={{ margin: 0 }}>Movimientos</h3>
                        <Button
                            label="Nuevo movimiento"
                            icon="pi pi-plus"
                            className="buttonMain"
                            onClick={handleOpenMovModal}
                        />
                    </div>

                    <DataTable
                        value={movements}
                        size="small"
                        stripedRows
                        lazy
                        paginator
                        rows={PAGE_SIZE}
                        first={firstMovement}
                        totalRecords={totalMovements}
                        onPage={onPage}
                        loading={loadingMovements}
                        emptyMessage="No hay movimientos para el rango seleccionado."
                    >
                        <Column field="dateTime" header="Fecha/Hora" body={(row: MovementResponseDTO) => formatDateTime(row.dateTime)} style={{ width: '160px' }} />
                        <Column field="username" header="Usuario" body={(row: MovementResponseDTO) => row.username ?? '—'} style={{ width: '120px' }} />
                        <Column field="financialAccountName" header="Cuenta" style={{ width: '150px' }} />
                        <Column field="movementTypeDescription" header="Tipo" style={{ width: '140px' }} />
                        <Column field="notes" header="Notas" body={(row: MovementResponseDTO) => row.notes ?? '—'} style={{ minWidth: '180px' }} />
                        <Column
                            field="amount"
                            header="Monto"
                            body={(row: MovementResponseDTO) => (
                                <span className="font-semibold" style={row.amount < 0 ? { color: '#DC3545' } : undefined}>
                                    {formatMoney(row.amount)}
                                </span>
                            )}
                            style={{ width: '130px', textAlign: 'right' }}
                        />
                    </DataTable>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#5A6676' }}>
                    <i className="pi pi-wallet" style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }} />
                    <p>Seleccione una caja para comenzar.</p>
                </div>
            )}

            {/* Modal nuevo movimiento */}
            <Modal
                visible={movModalVisible}
                setVisible={setMovModalVisible}
                header="Nuevo movimiento"
                width="w-medium"
                closable={!creatingMovement}
            >
                <div className="flex flex-column gap-3">
                    <div>
                        <Combobox
                            label="Cuenta financiera *"
                            list={movAccounts}
                            value={movSelectedAccount}
                            setValue={(v: CashRegisterFinancialAccountDTO | null) => {
                                setMovSelectedAccount(v);
                                setMovAmountError(null);
                            }}
                            optionLabel="financialAccountName"
                            placeholder="Seleccionar cuenta..."
                        />
                    </div>
                    <div>
                        <Combobox
                            label="Tipo de movimiento *"
                            list={movementTypes}
                            value={movType}
                            setValue={(v: MovementTypeOption | null) => setMovType(v)}
                            optionLabel="description"
                            placeholder="Seleccionar tipo..."
                        />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="flex flex-column gap-1">
                            <label htmlFor="rendition-movement-amount" className="font-medium" style={{ fontSize: '13px' }}>Monto *</label>
                            <InputNumber
                                inputId="rendition-movement-amount"
                                inputRef={movAmountRef}
                                value={movAmount}
                                onValueChange={e => {
                                    setMovAmount(e.value ?? null);
                                    setMovAmountError(null);
                                }}
                                mode="currency"
                                currency="ARS"
                                locale="es-AR"
                                min={movType?.id === 4 ? -999999999 : 0}
                                disabled={creatingMovement || !movType}
                                className={movAmountError ? 'field-error-target' : undefined}
                                aria-invalid={movAmountError ? true : undefined}
                                aria-describedby={movAmountError ? 'rendition-movement-amount-error' : undefined}
                            />
                            <FieldError id="rendition-movement-amount-error" message={movAmountError} />
                            {movType?.id === 4 && !movAmountError && (
                                <span style={{ fontSize: '11px', color: '#5A6676', marginTop: '2px' }}>
                                    <i className="pi pi-info-circle" style={{ fontSize: '11px', marginRight: '4px' }} />
                                    Ajuste: permite monto negativo
                                </span>
                            )}
                        </div>
                        <InputDate
                            label="Fecha"
                            value={movDate}
                            onChange={(e: any) => setMovDate(e.value ?? new Date())}
                        />
                    </div>
                    <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ fontSize: '13px' }}>Notas</label>
                        <InputTextarea
                            value={movNotes}
                            onChange={e => setMovNotes(e.target.value)}
                            rows={2}
                            autoResize
                            placeholder="Opcional"
                            disabled={creatingMovement}
                        />
                    </div>
                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            className="buttonMain3"
                            onClick={() => setMovModalVisible(false)}
                            disabled={creatingMovement}
                        />
                        <Button
                            label="Registrar movimiento"
                            icon="pi pi-check"
                            className="buttonMain"
                            onClick={handleCreateMovement}
                            loading={creatingMovement}
                            disabled={!movSelectedAccount || !movType || movAmount == null}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
